import { ZonedDateTime } from "@js-joda/core";
import { useMemo } from "react";
import { Alert } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useBillingPlanPaidCheck } from "../billing/useSubscription";
import {
	useSuspensePosts,
	useSuspensePostWithSiblings,
} from "../calendar/usePosts";
import { TimeFormatter, useTime } from "../common/time/useTime";
import {
	ButtonDropdownMenu,
	type DropdownActionProps,
} from "../components/ButtonDropdownMenu";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { FormPost } from "../post/FormPost";
import { FormPostProvider, useFormPostContext } from "../post/FormPostContext";

export const FormPostConsumer = () => {
	const { post, handleScheduleClick, handlePublishClick } =
		useFormPostContext();
	const isPlanPaid = useBillingPlanPaidCheck();

	const dropdownMenuItems: Array<DropdownActionProps> = useMemo(
		() => [
			{
				label: "Schedule",
				props: { onClick: handleScheduleClick },
			},
			{
				label: "Publish Now",
				props: { onClick: handlePublishClick },
			},
		],
		[handleScheduleClick, handlePublishClick],
	);

	const { format } = useTime();

	const formattedDate = format(
		TimeFormatter.Date,
		ZonedDateTime.parse(
			post?.scheduledAt || post?.updatedAt || ZonedDateTime.now().toString(),
		),
	);

	const title = `${formattedDate} ${formattedDate.length ? "|" : ""} ${post?.platform ?? ""}`;

	return (
		<AppPage
			title={title}
			headerActions={
				<ButtonDropdownMenu
					main={{
						label: "Schedule",
						props: {
							onClick: handleScheduleClick,
						},
					}}
					options={dropdownMenuItems}
				/>
			}
		>
			<p>
				<Link to={LinkGen.Calendar()}>Back to Calendar</Link>
			</p>
			{!isPlanPaid && (
				<Alert variant="warning">
					Posts are not published if you don't have a plan. Please view the{" "}
					<Link to={LinkGen.Settings.Billing()}>Billing Settings here</Link>!
				</Alert>
			)}
			<FormPost editing post={post} />
		</AppPage>
	);
};

export const PageBlogPost = () => {
	const { id } = useParams();
	const { IDs } = useSuspensePostWithSiblings(Number(id));
	const posts = useSuspensePosts(IDs);
	const siblings = posts.filter((post) => post.id !== Number(id));
	const post = posts.find((post) => post.id === Number(id));

	return (
		<FormPostProvider
			key={`blog-post-context-${post?.id}`}
			post={post}
			siblings={siblings}
			editing
			isBlog
		>
			<FormPostConsumer />
		</FormPostProvider>
	);
};
